import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI, TiersResponse } from '../services/user';
import { paymentAPI, PaymentConfig } from '../services/payment';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import CheckoutConfirmModal, { CheckoutItemKind } from '../components/CheckoutConfirmModal';

interface PendingCheckout {
  productName: string;
  price: number;
  kind: CheckoutItemKind;
  /** Function that performs the actual API call + redirect */
  execute: () => Promise<{ redirectUri: string }>;
}

export default function PricingPage() {
  const { user, isAuthenticated } = useAuth();
  const [tiers, setTiers] = useState<TiersResponse | null>(null);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingCheckout, setPendingCheckout] = useState<PendingCheckout | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tiersData, paymentData] = await Promise.all([
          userAPI.getTiers(),
          paymentAPI.getConfig(),
        ]);
        setTiers(tiersData);
        setPaymentConfig(paymentData);
      } catch {
        // Use fallback data if fetch fails
        setTiers({
          tiers: [
            {
              id: 'free',
              name: t('pricing.free'),
              price: 0,
              features: {
                monthlyAnalyses: 3,
                maxDocumentSize: 25000,
                maxJudgments: 10,
                pdfExport: false,
                docxExport: false,
                priorityProcessing: false,
                includedModels: ['gpt-5-mini'],
                extraModels: [],
              },
            },
            {
              id: 'pro',
              name: t('pricing.pro'),
              price: 49,
              features: {
                monthlyAnalyses: 30,
                maxDocumentSize: 50000,
                maxJudgments: 30,
                pdfExport: true,
                docxExport: false,
                priorityProcessing: false,
                includedModels: ['gpt-5-mini', 'claude-haiku'],
                extraModels: ['gpt-5', 'gpt-5.2'],
              },
            },
            {
              id: 'max',
              name: t('pricing.max'),
              price: 249,
              features: {
                monthlyAnalyses: 100,
                maxDocumentSize: 200000,
                maxJudgments: 50,
                pdfExport: true,
                docxExport: true,
                priorityProcessing: true,
                includedModels: ['gpt-5-mini', 'claude-haiku', 'gpt-5', 'gpt-5.2', 'claude-sonnet', 'claude-opus'],
                extraModels: [],
              },
            },
          ],
          credits: { pricePerTen: 50 },
          modelCredits: {
            'gpt-5-mini': 0,
            'claude-haiku': 0,
            'gpt-5': 1,
            'gpt-5.2': 2,
            'claude-sonnet': 3,
            'claude-opus': 4,
          },
          judgmentBoostCredits: { 10: 1, 20: 2, 30: 3 },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [t]);

  // Step 1 — open the confirmation modal (no API call yet, no redirect yet).
  // Required by Polish ustawa o prawach konsumenta art. 17 ust. 3 ("button rule"):
  // user must see order summary + obligation-to-pay info BEFORE the final action.
  const openProCheckout = () => {
    if (!paymentConfig?.enabled) {
      setError(t('pricing.paymentsUnavailable'));
      return;
    }
    setError(null);
    setPendingCheckout({
      productName: tiers?.tiers.find((tier) => tier.id === 'pro')?.name || t('pricing.pro'),
      price: tiers?.tiers.find((tier) => tier.id === 'pro')?.price || 49,
      kind: 'subscription',
      execute: () => paymentAPI.createProCheckout(),
    });
  };

  const openMaxCheckout = () => {
    if (!paymentConfig?.enabled) {
      setError(t('pricing.paymentsUnavailable'));
      return;
    }
    setError(null);
    setPendingCheckout({
      productName: tiers?.tiers.find((tier) => tier.id === 'max')?.name || t('pricing.max'),
      price: tiers?.tiers.find((tier) => tier.id === 'max')?.price || 249,
      kind: 'subscription',
      execute: () => paymentAPI.createMaxCheckout(),
    });
  };

  const openCreditsCheckout = () => {
    if (!paymentConfig?.enabled) {
      setError(t('pricing.paymentsUnavailable'));
      return;
    }
    setError(null);
    setPendingCheckout({
      productName: `${t('pricing.checkoutConfirm.creditsTitle')} — ${t('pricing.checkoutConfirm.creditsAmount')}`,
      price: tiers?.credits.pricePerTen || 50,
      kind: 'credits',
      execute: () => paymentAPI.createCreditsCheckout(1),
    });
  };

  // Step 2 — user confirmed in the modal: actually call the API and redirect to PayU.
  const confirmCheckout = async () => {
    if (!pendingCheckout) return;
    setCheckoutLoading('confirm');
    try {
      const { redirectUri } = await pendingCheckout.execute();
      window.location.href = redirectUri;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pricing.checkoutError'));
      setCheckoutLoading(null);
      setPendingCheckout(null);
    }
  };

  const cancelCheckout = () => {
    setPendingCheckout(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <svg className="w-8 h-8 animate-spin mx-auto text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="mt-2 text-slate-500 dark:text-slate-400">{t('pricing.loading')}</p>
        </div>
      </div>
    );
  }

  const freeTier = tiers?.tiers.find((t) => t.id === 'free');
  const proTier = tiers?.tiers.find((t) => t.id === 'pro');
  const maxTier = tiers?.tiers.find((t) => t.id === 'max');
  const currentTier = user?.tier || 'free';

  const CheckIcon = () => (
    <svg className="w-5 h-5 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  const XIcon = () => (
    <svg className="w-5 h-5 text-slate-300 dark:text-slate-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  // Helper to format document size with A4 equivalent
  const formatDocSize = (chars: number) => {
    const k = chars / 1000;
    const pages = Math.round(chars / 8000); // ~8000 chars per A4 page
    return { value: `${k}k`, desc: t('pricing.features.pagesA4', { pages }) };
  };

  const freeDocSize = formatDocSize(freeTier?.features.maxDocumentSize || 25000);
  const proDocSize = formatDocSize(proTier?.features.maxDocumentSize || 50000);
  const maxDocSize = formatDocSize(maxTier?.features.maxDocumentSize || 200000);

  const features = [
    {
      label: t('pricing.features.monthlyAnalyses'),
      free: freeTier?.features.monthlyAnalyses,
      pro: proTier?.features.monthlyAnalyses,
      max: maxTier?.features.monthlyAnalyses
    },
    {
      label: t('pricing.features.maxDocumentSize'),
      free: freeDocSize.value,
      freeDesc: freeDocSize.desc,
      pro: proDocSize.value,
      proDesc: proDocSize.desc,
      max: maxDocSize.value,
      maxDesc: maxDocSize.desc,
      hasDesc: true
    },
    {
      label: t('pricing.features.maxJudgments'),
      free: freeTier?.features.maxJudgments,
      pro: proTier?.features.maxJudgments,
      max: maxTier?.features.maxJudgments
    },
    {
      label: t('pricing.features.pdfExport'),
      free: false,
      pro: true,
      max: true,
      isBoolean: true
    },
    {
      label: t('pricing.features.docxExport'),
      free: false,
      pro: false,
      max: true,
      isBoolean: true
    },
    {
      label: t('pricing.features.aiModelChoice'),
      free: t('pricing.features.oneModel'),
      pro: t('pricing.features.fourModels'),
      max: t('pricing.features.allSix')
    },
    {
      label: t('pricing.features.priorityProcessing'),
      free: false,
      pro: false,
      max: true,
      isBoolean: true
    },
    {
      label: t('pricing.features.unlimitedSearch'),
      free: true,
      pro: true,
      max: true,
      isBoolean: true
    },
  ];

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Strona główna', item: 'https://analizaprawna.eu' },
      { '@type': 'ListItem', position: 2, name: 'Cennik', item: 'https://analizaprawna.eu/pricing' },
    ],
  };

  return (
    <div className="max-w-5xl mx-auto">
      <SEOHead
        title="Cennik | AnalizaPrawna"
        description="Sprawdź plany i cennik AnalizaPrawna. Darmowe wyszukiwanie orzeczeń, plan Pro od 49 PLN/mies., plan Max od 249 PLN/mies."
        canonicalUrl="https://analizaprawna.eu/pricing"
        jsonLd={breadcrumbLd}
      />
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
          {t('pricing.title')}
        </h1>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          {t('pricing.subtitle')}
        </p>
      </div>

      {/* Pricing Table */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mb-8">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-auto" />
            <col className="w-[170px]" />
            <col className="w-[170px]" />
            <col className="w-[170px]" />
          </colgroup>
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="py-8 px-4 text-left"></th>
              <th className="py-8 px-4 text-center align-top">
                <div className="flex flex-col items-center">
                  <div className="h-6 mb-2">
                    {currentTier === 'free' && (
                      <span className="inline-block px-2 py-1 text-xs font-semibold bg-blue-600 text-white rounded-full">
                        {t('pricing.yourPlan')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-bold text-slate-900 dark:text-white">
                    {freeTier?.name || t('pricing.free')}
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                    0 PLN
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{t('pricing.perMonth')}</div>
                </div>
              </th>
              <th className="py-8 px-4 text-center align-top bg-gradient-to-b from-amber-50 to-amber-50/30 dark:from-amber-900/30 dark:to-amber-900/10">
                <div className="flex flex-col items-center">
                  <div className="h-6 mb-2">
                    {currentTier === 'pro' ? (
                      <span className="inline-block px-2 py-1 text-xs font-semibold bg-amber-500 text-white rounded-full">
                        {t('pricing.yourPlan')}
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-1 text-xs font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full uppercase tracking-wide">
                        {t('pricing.recommended')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-bold text-slate-900 dark:text-white">
                    {proTier?.name || t('pricing.pro')}
                  </div>
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                    {proTier?.price || 49} PLN
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{t('pricing.perMonth')}</div>
                </div>
              </th>
              <th className="py-8 px-4 text-center align-top bg-gradient-to-b from-purple-50 to-purple-50/30 dark:from-purple-900/30 dark:to-purple-900/10">
                <div className="flex flex-col items-center">
                  <div className="h-6 mb-2">
                    {currentTier === 'max' ? (
                      <span className="inline-block px-2 py-1 text-xs font-semibold bg-purple-600 text-white rounded-full">
                        {t('pricing.yourPlan')}
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-1 text-xs font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full uppercase tracking-wide">
                        Premium
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-bold text-slate-900 dark:text-white">
                    {maxTier?.name || t('pricing.max')}
                  </div>
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                    {maxTier?.price || 249} PLN
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{t('pricing.perMonth')}</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map((f, index) => (
              <tr
                key={f.label}
                className={index < features.length - 1 ? 'border-b border-slate-100 dark:border-slate-700/50' : ''}
              >
                <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">
                  {f.label}
                </td>
                <td className="py-3 px-4 text-center">
                  {f.isBoolean ? (
                    f.free ? <CheckIcon /> : <XIcon />
                  ) : (
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-sm text-blue-600 dark:text-blue-400 whitespace-nowrap">
                        {f.free}
                      </span>
                      {f.hasDesc && f.freeDesc && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {f.freeDesc}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="py-3 px-4 text-center bg-amber-50/30 dark:bg-amber-900/5">
                  {f.isBoolean ? (
                    f.pro ? <CheckIcon /> : <XIcon />
                  ) : (
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-sm text-amber-600 dark:text-amber-400 whitespace-nowrap">
                        {f.pro}
                      </span>
                      {f.hasDesc && f.proDesc && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {f.proDesc}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="py-3 px-4 text-center bg-purple-50/30 dark:bg-purple-900/5">
                  {f.isBoolean ? (
                    f.max ? <CheckIcon /> : <XIcon />
                  ) : (
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-sm text-purple-600 dark:text-purple-400 whitespace-nowrap">
                        {f.max}
                      </span>
                      {f.hasDesc && f.maxDesc && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {f.maxDesc}
                        </span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {/* Action row */}
            <tr className="border-t border-slate-200 dark:border-slate-700">
              <td className="py-6 px-4"></td>
              <td className="py-6 px-4 text-center">
                {!isAuthenticated ? (
                  <Link
                    to="/register"
                    className="inline-block py-2 px-4 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-900 dark:text-white font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
                  >
                    {t('pricing.signUp')}
                  </Link>
                ) : currentTier === 'free' ? (
                  <span className="inline-block py-2 px-3 text-slate-500 dark:text-slate-400 font-medium text-sm whitespace-nowrap">
                    {t('pricing.activePlan')}
                  </span>
                ) : null}
              </td>
              <td className="py-6 px-4 text-center bg-amber-50/30 dark:bg-amber-900/5">
                {!isAuthenticated ? (
                  <Link
                    to="/register"
                    className="inline-block py-2 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
                  >
                    {t('pricing.choosePro')}
                  </Link>
                ) : currentTier === 'pro' ? (
                  <span className="inline-block py-2 px-3 text-amber-600 dark:text-amber-400 font-medium text-sm whitespace-nowrap">
                    {t('pricing.activePlan')}
                  </span>
                ) : currentTier === 'max' ? (
                  <span className="inline-block py-2 px-3 text-slate-400 font-medium text-xs whitespace-nowrap">
                    {t('pricing.haveMax')}
                  </span>
                ) : (
                  <button
                    onClick={openProCheckout}
                    className="inline-block py-2 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
                  >
                    {t('pricing.upgradeToPro')}
                  </button>
                )}
              </td>
              <td className="py-6 px-4 text-center bg-purple-50/30 dark:bg-purple-900/5">
                {!isAuthenticated ? (
                  <Link
                    to="/register"
                    className="inline-block py-2 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
                  >
                    {t('pricing.chooseMax')}
                  </Link>
                ) : currentTier === 'max' ? (
                  <span className="inline-block py-2 px-3 text-purple-600 dark:text-purple-400 font-medium text-sm whitespace-nowrap">
                    {t('pricing.activePlan')}
                  </span>
                ) : (
                  <button
                    onClick={openMaxCheckout}
                    className="inline-block py-2 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
                  >
                    {t('pricing.upgradeToMax')}
                  </button>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {error && (
        <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-400 text-center">{error}</p>
        </div>
      )}

      {/* Credits Section */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 mb-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              {t('pricing.credits.title')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              {t('pricing.credits.description')}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="text-center md:text-right mb-4">
              <span className="text-3xl font-bold text-slate-900 dark:text-white">
                {tiers?.credits.pricePerTen || 50} PLN
              </span>
              <span className="text-slate-500 dark:text-slate-400 ml-2">{t('pricing.credits.perTen')}</span>
            </div>
            {isAuthenticated ? (
              <button
                onClick={openCreditsCheckout}
                className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('pricing.credits.buy')}
              </button>
            ) : (
              <Link
                to="/register"
                className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                {t('pricing.signUp')}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white text-center mb-8">
          {t('pricing.faq.title')}
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
              {t('pricing.faq.q1')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {t('pricing.faq.a1')}
            </p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
              {t('pricing.faq.q2')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {t('pricing.faq.a2')}
            </p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
              {t('pricing.faq.q3')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {t('pricing.faq.a3')}
            </p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
              {t('pricing.faq.q4')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {t('pricing.faq.a4')}
            </p>
          </div>
        </div>
      </div>

      {/* Checkout confirmation modal — required by Polish consumer law (button rule) */}
      <CheckoutConfirmModal
        isOpen={pendingCheckout !== null}
        onClose={cancelCheckout}
        onConfirm={confirmCheckout}
        productName={pendingCheckout?.productName || ''}
        price={pendingCheckout?.price || 0}
        kind={pendingCheckout?.kind || 'subscription'}
        isLoading={checkoutLoading === 'confirm'}
      />
    </div>
  );
}
